import React, { useState, useEffect } from "react";
import { Card, Input } from "antd";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from '../components/StandardButton';
import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";

const EmailVerifier = () => {
  const [email, setEmail] = useState("example@example.com");
  const [output, setOutput] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const id = searchParams.get("share");
    if (id) {
      api.get(`/email-verify/share/${id}`)
        .then((res) => {
          const data = res.data;
          if (data && data.email) {
            setEmail(data.email);
            setOutput(data.output);
          }
        })
        .catch(() => {
          toast.error(t("server_error"));
        });
    }
  }, []);

  const handleVerify = async () => {
    if (!email) return;
    setLoading(true);
    const toastId = toast.loading(t("sending_request"));

    try {
      const res = await api.post(`/email-verify`, { email });
      const data = res.data;
      setOutput(data);

      if (data.success) {
        toast.success(t("validation_success"));
      } else {
        toast.error(t("validation_failed"));
      }
    } catch (error) {
      toast.error(t("server_error"));
    } finally {
      setLoading(false);
      toast.dismiss(toastId);
    }
  };

  const handleShare = async () => {
    try {
      const response = await api.post(`/email-verify/share`, {
        email,
        output,
      });
      const data = response.data;
      if (data.id) {
        const url = `${window.location.origin}/tools/email-verifier?share=${data.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t('share_success'));
      }
    } catch (err) {
      toast.error(t('share_failed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg rounded-xl p-4 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-indigo-700 mb-2">{t('validate_email_placeholder')}</h2>
            <Input
              placeholder={t('validate_email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <StandardButton
              type="secondary"
              className="w-full mt-4"
              onClick={handleVerify}
              loading={loading}
            >
              {t('verify_button')}
            </StandardButton>
          </div>

          {output && (
            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 whitespace-pre-wrap">
              <strong className="text-indigo-700">{t('result_label')}</strong>
              <div className="mt-2 space-y-2">
                <p><strong>{t('syntax_check_result')}:</strong> {output.validators?.syntax ? "✅" : "❌"}</p>
                <p><strong>{t('mx_check_result')}:</strong> {output.validators?.mx ? "✅" : "❌"}</p>
                <p><strong>{t('typo_check_label')}:</strong> {output.validators?.typo === true ? "✅" : t('possible_typo')}</p>
                <p><strong>{t('overall_result_label')}:</strong> {output.success ? "✅" : t('email_failed_validation', { reason: output.reason })}</p>
              </div>

              <div className="mt-4 flex gap-3">
                <StandardButton
                  type="secondary"
                  size="small"
                  onClick={handleShare}
                >
                  {t('share')}
                </StandardButton>
              </div>
            </div>
          )}
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default EmailVerifier;